import { useEffect, useRef } from 'react'
import { cn } from '@/lib/cn'
import { BUILTIN_COMMANDS, body, frontmatter, type KitItem } from './claudeKit'

export type SlashOption = { name: string; description: string; hint?: string; source: 'Claude Code' | 'Project command' | 'Project skill'
  /** The start of what it tells Claude, for the preview. */
  preview?: string }

export function slashOptions(kit: KitItem[]): SlashOption[] {
  const own: SlashOption[] = kit.filter((k) => k.kind !== 'agent').map((k) => ({
    name: k.name, description: k.description, hint: frontmatter(k.text)['argument-hint'] || undefined, preview: body(k.text).slice(0, 1200),
    source: k.kind === 'command' ? 'Project command' : 'Project skill',
  }))
  // The project's own come first, and win over a built-in of the same name.
  const names = new Set(own.map((o) => o.name))
  return [...own, ...BUILTIN_COMMANDS.filter((b) => !names.has(b.name)).map((b) => ({ ...b, source: 'Claude Code' as const }))]
}

/** The commands matching what is typed after `/`, best first: name prefix, then anywhere. */
export function matchSlash(all: SlashOption[], typed: string): SlashOption[] {
  const q = typed.toLowerCase()
  const pre = all.filter((o) => o.name.startsWith(q))
  const rest = all.filter((o) => !o.name.startsWith(q) && (o.name.includes(q) || o.description.toLowerCase().includes(q)))
  return [...pre, ...rest].slice(0, 12)
}

/** Above the message box while it holds `/word`: arrows move, Enter or Tab picks, Esc closes. */
export function SlashMenu({ options, active, onPick, onHover }: { options: SlashOption[]; active: number; onPick: (o: SlashOption) => void; onHover: (i: number) => void }) {
  const list = useRef<HTMLDivElement>(null)
  useEffect(() => { list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }) }, [active])
  if (!options.length) return null
  const cur = options[active]
  return (
    <div className="absolute inset-x-0 bottom-full z-20 mb-2 flex max-h-72 overflow-hidden rounded-lg border bg-popover shadow-lg">
    <div ref={list} role="listbox" aria-label="Commands" className="min-w-0 flex-1 overflow-y-auto p-1">
      {options.map((o, i) => (
        <div key={`${o.source}:${o.name}`} role="option" aria-selected={i === active} data-i={i}
          onMouseDown={(e) => { e.preventDefault(); onPick(o) }} onMouseEnter={() => onHover(i)}
          className={cn('flex cursor-pointer items-baseline gap-2 rounded-md px-2.5 py-1.5 text-xs', i === active && 'bg-accent/15')}>
          <span className="shrink-0 font-mono text-foreground">/{o.name}</span>
          {o.hint && <span className="shrink-0 font-mono text-fg-faint">{o.hint}</span>}
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{o.description}</span>
          <span className="shrink-0 text-[10px] text-fg-faint">{o.source}</span>
        </div>
      ))}
    </div>
    {/* What the highlighted one does; it follows the mouse and the arrows. */}
    {cur && <div className="hidden w-80 shrink-0 overflow-y-auto border-l bg-surface-1/60 p-3 md:block"><SlashPreview o={cur} /></div>}
    </div>
  )
}

export function SlashPreview({ o }: { o: SlashOption }) {
  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="flex items-baseline gap-2"><span className="font-mono font-medium text-foreground">/{o.name}</span>{o.hint && <span className="font-mono text-fg-faint">{o.hint}</span>}</div>
      <div className="text-[10px] text-fg-subtle">{o.source}</div>
      {o.description ? <p className="leading-relaxed text-muted-foreground">{o.description}</p> : <p className="text-fg-faint">No description.</p>}
      {o.preview && <pre className="max-h-40 overflow-hidden whitespace-pre-wrap break-words rounded-md border bg-bg p-2 font-mono text-[10.5px] leading-snug text-fg-subtle">{o.preview}</pre>}
    </div>
  )
}
